import { 
  collection, 
  query, 
  where, 
  Timestamp, 
  orderBy, 
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

// Interfaces pour les données statistiques
export interface ItemSalesStat {
  id: string;
  name: string;
  quantity: number;
  revenue: number;
  categoryId?: string;
  categoryName?: string;
  bundleCount?: number;   // Nombre de fois où l'item apparaît dans un bundle
  singleCount?: number;   // Nombre de fois où l'item est vendu individuellement
  bundlePercentage?: number; // Pourcentage d'apparition dans les bundles
}

export interface DailySalesStats {
  id: string;
  date: Timestamp;
  totalOrders: number;
  totalRevenue: number;
  totalItems: number;
  popularItems: ItemSalesStat[];
  itemsByCategory: {[key: string]: ItemSalesStat[]};
  createdAt: Timestamp;
}

export interface WeeklySalesStats {
  id: string;
  startDate: Timestamp;
  endDate: Timestamp;
  totalOrders: number;
  totalRevenue: number;
  totalItems: number;
  dailyRevenue: {[key: string]: number};
  popularItems: ItemSalesStat[];
  itemsByCategory: {[key: string]: ItemSalesStat[]};
  createdAt: Timestamp;
}

export interface MonthlySalesStats {
  id: string;
  month: number;
  year: number;
  startDate: Timestamp;
  endDate: Timestamp;
  totalOrders: number;
  totalRevenue: number;
  totalItems: number;
  weeklyRevenue: {[key: string]: number};
  dailyRevenue: {[key: string]: number};
  popularItems: ItemSalesStat[];
  itemsByCategory: {[key: string]: ItemSalesStat[]};
  createdAt: Timestamp;
}

export class SalesStatsService {
  private readonly statsPath: string;
  private readonly foodTruckId: string;

  constructor(foodTruckId: string) {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    this.foodTruckId = foodTruckId;
    this.statsPath = `foodTrucks/${foodTruckId}/stats`;
  }

  /**
   * Récupère les statistiques journalières pour une date spécifique
   * @param date - Date pour laquelle récupérer les statistiques
   * @returns Promise contenant les statistiques journalières
   */
  async getDailyStats(date: Date = new Date()): Promise<DailySalesStats | null> {
    try {
      const dayId = format(date, 'yyyy-MM-dd');
      const statsDoc = await getDoc(doc(db, this.statsPath, dayId));
      
      if (!statsDoc.exists()) {
        console.log(`Pas de statistiques pour ${dayId}`);
        return null;
      }
      
      return statsDoc.data() as DailySalesStats;
    } catch (error) {
      console.error('Error getting daily stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques de vente pour une période donnée
   * @param startDate - Date de début de la période
   * @param endDate - Date de fin de la période
   */
  async getSalesStats(startDate: Date, endDate: Date): Promise<DailySalesStats[]> {
    try {
      // Format des dates pour Firestore
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      // Requête pour obtenir tous les documents de statistiques dans la période
      const statsQuery = query(
        collection(db, this.statsPath),
        where('id', '>=', startDateStr),
        where('id', '<=', endDateStr),
        orderBy('id', 'asc')
      );
      
      const snapshot = await getDocs(statsQuery);
      
      // Si pas de résultats, retourner un tableau vide
      if (snapshot.empty) {
        return [];
      }
      
      // Convertir les documents en objets DailySalesStats
      const stats = snapshot.docs.map(doc => doc.data() as DailySalesStats);
      
      // S'assurer que tous les jours de la période sont représentés
      const allDaysInRange = eachDayOfInterval({ start: startDate, end: endDate });
      const statsByDay: { [key: string]: DailySalesStats } = {};
      
      // Indexer les stats existantes par jour
      stats.forEach(dayStats => {
        const day = format(dayStats.date.toDate(), 'yyyy-MM-dd');
        statsByDay[day] = dayStats;
      });
      
      // Créer un tableau final avec des stats pour chaque jour, ou des stats vides si nécessaire
      const result = allDaysInRange.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        return statsByDay[dayStr] || this.createEmptyDailyStats(day);
      });
      
      return result;
    } catch (error) {
      console.error('Error getting sales stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques hebdomadaires
   * @param date - Date dans la semaine pour laquelle récupérer les statistiques
   */
  async getWeeklyStats(date: Date = new Date()): Promise<WeeklySalesStats> {
    try {
      // Calculer le début et la fin de la semaine
      const start = startOfWeek(date, { locale: fr });
      const end = endOfWeek(date, { locale: fr });
      
      // Récupérer les statistiques journalières pour cette semaine
      const dailyStats = await this.getSalesStats(start, end);
      
      // Calculer les statistiques hebdomadaires
      return this.calculateWeeklyStats(dailyStats, start, end);
    } catch (error) {
      console.error('Error getting weekly stats:', error);
      throw error;
    }
  }

  /**
   * Récupère les statistiques mensuelles
   * @param date - Date dans le mois pour laquelle récupérer les statistiques
   */
  async getMonthlyStats(date: Date = new Date()): Promise<MonthlySalesStats> {
    try {
      // Calculer le début et la fin du mois
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      
      // Récupérer les statistiques journalières pour ce mois
      const dailyStats = await this.getSalesStats(start, end);
      
      // Calculer les statistiques mensuelles
      return this.calculateMonthlyStats(dailyStats, start, end);
    } catch (error) {
      console.error('Error getting monthly stats:', error);
      throw error;
    }
  }

  /**
   * Crée un objet de statistiques vide pour un jour sans données
   * @private
   * @param date - Date pour laquelle créer les statistiques vides
   * @returns Statistiques journalières vides
   */
  private createEmptyDailyStats(date: Date): DailySalesStats {
    return {
      id: format(date, 'yyyy-MM-dd'),
      date: Timestamp.fromDate(date),
      totalOrders: 0,
      totalRevenue: 0,
      totalItems: 0,
      popularItems: [],
      itemsByCategory: {},
      createdAt: Timestamp.now()
    };
  }

  /**
   * Calcule les statistiques de vente hebdomadaires à partir des statistiques journalières
   * @private
   * @param dailyStats - Liste des statistiques journalières
   * @param startDate - Date de début de la semaine
   * @param endDate - Date de fin de la semaine
   * @returns Statistiques de vente de la semaine
   */
  private calculateWeeklyStats(dailyStats: DailySalesStats[], startDate: Date, endDate: Date): WeeklySalesStats {
    let totalOrders = 0;
    let totalRevenue = 0;
    let totalItems = 0;
    const dailyRevenue: {[key: string]: number} = {};
    
    // Map pour agréger les statistiques par item
    const itemStatsMap = new Map<string, ItemSalesStat>();
    
    dailyStats.forEach(day => {
      totalOrders += day.totalOrders;
      totalRevenue += day.totalRevenue;
      totalItems += day.totalItems;
      
      const dateKey = format(day.date.toDate(), 'yyyy-MM-dd');
      dailyRevenue[dateKey] = day.totalRevenue;
      
      // Agréger les statistiques des items populaires
      day.popularItems.forEach(item => {
        const existing = itemStatsMap.get(item.id) || {
          id: item.id,
          name: item.name,
          quantity: 0,
          revenue: 0,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          bundleCount: 0,
          singleCount: 0
        };
        
        existing.quantity += item.quantity;
        existing.revenue += item.revenue;
        
        // Agréger les compteurs de bundle si disponibles
        if (item.bundleCount !== undefined) {
          existing.bundleCount = (existing.bundleCount || 0) + item.bundleCount;
        }
        if (item.singleCount !== undefined) {
          existing.singleCount = (existing.singleCount || 0) + item.singleCount;
        }
        
        itemStatsMap.set(item.id, existing);
      });
    });
    
    // Convertir la Map en tableau et trier par quantité
    const popularItems = Array.from(itemStatsMap.values())
      .sort((a, b) => b.quantity - a.quantity)
      .map(item => {
        // Recalculer le pourcentage de bundle
        if (item.bundleCount !== undefined && item.singleCount !== undefined) {
          item.bundlePercentage = item.quantity > 0 ? 
            Math.round((item.bundleCount / item.quantity) * 100) : 0;
        }
        return item;
      });
    
    // Organiser les items par catégorie
    const itemsByCategory: {[key: string]: ItemSalesStat[]} = {};
    
    popularItems.forEach(item => {
      const categoryId = item.categoryId || 'uncategorized';
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      itemsByCategory[categoryId].push(item);
    });
    
    const weekId = `${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`;
    const now = Timestamp.now();

    return {
      id: weekId,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      totalOrders,
      totalRevenue,
      totalItems,
      dailyRevenue,
      popularItems,
      itemsByCategory,
      createdAt: now
    };
  }

  /**
   * Calcule les statistiques de vente mensuelles à partir des statistiques journalières
   * @private
   * @param dailyStats - Liste des statistiques journalières
   * @param startDate - Date de début du mois
   * @param endDate - Date de fin du mois
   * @returns Statistiques de vente du mois
   */
  private calculateMonthlyStats(dailyStats: DailySalesStats[], startDate: Date, endDate: Date): MonthlySalesStats {
    let totalOrders = 0;
    let totalRevenue = 0;
    let totalItems = 0;
    const dailyRevenue: {[key: string]: number} = {};
    const weeklyRevenue: {[key: string]: number} = {};
    
    // Map pour agréger les statistiques par item
    const itemStatsMap = new Map<string, ItemSalesStat>();
    
    // Organiser les jours par semaine
    const daysByWeek: {[key: string]: DailySalesStats[]} = {};
    
    dailyStats.forEach(day => {
      totalOrders += day.totalOrders;
      totalRevenue += day.totalRevenue;
      totalItems += day.totalItems;
      
      const dateKey = format(day.date.toDate(), 'yyyy-MM-dd');
      dailyRevenue[dateKey] = day.totalRevenue;
      
      // Déterminer la semaine du jour
      const dayDate = day.date.toDate();
      const weekStart = startOfWeek(dayDate, { locale: fr });
      const weekEnd = endOfWeek(dayDate, { locale: fr });
      const weekKey = `${format(weekStart, 'yyyy-MM-dd')}_${format(weekEnd, 'yyyy-MM-dd')}`;
      
      if (!daysByWeek[weekKey]) {
        daysByWeek[weekKey] = [];
      }
      
      daysByWeek[weekKey].push(day);
      
      // Agréger les statistiques des items populaires
      day.popularItems.forEach(item => {
        const existing = itemStatsMap.get(item.id) || {
          id: item.id,
          name: item.name,
          quantity: 0,
          revenue: 0,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          bundleCount: 0,
          singleCount: 0
        };
        
        existing.quantity += item.quantity;
        existing.revenue += item.revenue;
        
        // Agréger les compteurs de bundle si disponibles
        if (item.bundleCount !== undefined) {
          existing.bundleCount = (existing.bundleCount || 0) + item.bundleCount;
        }
        if (item.singleCount !== undefined) {
          existing.singleCount = (existing.singleCount || 0) + item.singleCount;
        }
        
        itemStatsMap.set(item.id, existing);
      });
    });
    
    // Calculer le chiffre d'affaires par semaine
    Object.entries(daysByWeek).forEach(([weekKey, weekDays]) => {
      const weekRevenue = weekDays.reduce((sum, day) => sum + day.totalRevenue, 0);
      weeklyRevenue[weekKey] = weekRevenue;
    });
    
    // Convertir la Map en tableau et trier par quantité
    const popularItems = Array.from(itemStatsMap.values())
      .sort((a, b) => b.quantity - a.quantity)
      .map(item => {
        // Recalculer le pourcentage de bundle
        if (item.bundleCount !== undefined && item.singleCount !== undefined) {
          item.bundlePercentage = item.quantity > 0 ? 
            Math.round((item.bundleCount / item.quantity) * 100) : 0;
        }
        return item;
      });
    
    // Organiser les items par catégorie
    const itemsByCategory: {[key: string]: ItemSalesStat[]} = {};
    
    popularItems.forEach(item => {
      const categoryId = item.categoryId || 'uncategorized';
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      itemsByCategory[categoryId].push(item);
    });
    
    const month = startDate.getMonth() + 1; // Les mois commencent à 0 en JavaScript
    const year = startDate.getFullYear();
    const monthId = `${year}-${month.toString().padStart(2, '0')}`;
    const now = Timestamp.now();

    return {
      id: monthId,
      month,
      year,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      totalOrders,
      totalRevenue,
      totalItems,
      weeklyRevenue,
      dailyRevenue,
      popularItems,
      itemsByCategory,
      createdAt: now
    };
  }
}

// Export singleton instance factory
export const salesStatsService = {
  getInstance(foodTruckId: string): SalesStatsService {
    if (!foodTruckId) {
      throw new Error('Food truck ID is required');
    }
    return new SalesStatsService(foodTruckId);
  }
};